import { useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import { Organization } from "@/api/types/organization"
import EditInfoModal from "@/components/modals/organization/EditInfoModal"
import ScreenWrapper from "@/components/ScreenWrapper"
import {
  DataTable,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  selectColumn,
  createSortableColumn,
  createActionsColumn,
  useExportMutation,
  Button,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  ORGANIZATION_STORAGE_KEY,
  useOrganizationFilters,
} from "@/hooks/filters/useOrganizationFilters"
import EditStyleModal from "@/components/modals/organization/EditStyleModal"
import { organizationQueryKeys } from "@/api/query-keys/organizations"
import ArchiveOrganizationModal from "@/components/modals/organization/ArchiveOrganizationModal"
import UnarchiveOrganizationModal from "@/components/modals/organization/UnarchiveOrganizationModal"
import { CreateOrganizationUserModal } from "@/components/modals/organization/CreateOrganizationUserModal"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"

export const Route = createFileRoute("/beheer/organisaties/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "organizations"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const ALL_COLUMN_IDS = ["id", "name", "email", "kvkNumber", "vatNumber"]

function RouteComponent() {
  const [editInfoOpen, setEditInfoOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Organization | null>(null)
  const [editStyleOpen, setEditStyleOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false)
  const router = useRouter()
  const queryClient = useQueryClient()

  const searchParams = useSearch({ from: "/beheer/organisaties/" })
  const navigate = useNavigate({
    from: "/beheer/organisaties/",
  })

  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useOrganizationFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    page,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...organizationQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: ORGANIZATION_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: organizationQueryKeys.filters({
        page,
        pageSize: TABLE_PAGE_SIZE,
        search,
        sortField,
        sortDirection,
      }),
    })
  }

  const closeEditInfoDialog = () => {
    setEditInfoOpen(false)
    setActiveRow(null)
    invalidateQuery()
  }

  const closeEditStyleDialog = () => {
    setEditStyleOpen(false)
    setActiveRow(null)
    invalidateQuery()
  }

  const handleNavigateDetail = (organizationId: Organization["id"]) => {
    router.navigate({
      to: "/beheer/organisaties/$organizationId/gegevens",
      params: { organizationId: String(organizationId) },
      search: { from: router.state.location.href },
    })
  }

  const columns: ColumnDef<Organization>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "name", name: "Naam" }),
    createSortableColumn({ accessorKey: "email", name: "E-mail" }),
    createSortableColumn({ accessorKey: "kvkNumber", name: "KvK nummer" }),
    createSortableColumn({ accessorKey: "vatNumber", name: "BTW nummer" }),
    createActionsColumn([
      {
        label: "Gegevens wijzigen",
        onClick: (e, organization) => {
          e.stopPropagation()
          setActiveRow(organization)
          setEditInfoOpen(true)
        },
      },
      {
        label: "Nieuw contactpersoon",
        onClick: (e, organization) => {
          e.stopPropagation()
          setActiveRow(organization)
          setIsCreateContactOpen(true)
        },
      },
      {
        label: "Huisstijl wijzigen",
        onClick: (e, organization) => {
          e.stopPropagation()
          setActiveRow(organization)
          setEditStyleOpen(true)
        },
      },
      ...(archived
        ? [
            {
              label: "Organisatie herstellen",
              onClick: (e: React.MouseEvent, organization: Organization) => {
                e.stopPropagation()
                setActiveRow(organization)
                setIsUnarchiveOpen(true)
              },
            },
          ]
        : [
            {
              label: "Organisatie archiveren",
              onClick: (e: React.MouseEvent, organization: Organization) => {
                e.stopPropagation()
                setActiveRow(organization)
                setIsArchiveOpen(true)
              },
            },
          ]),
    ]),
  ]

  const { mutate: exportOrganizations } = useExportMutation({
    exportApiPrefix: "organizations",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Organisaties"
        data={data?.data.organizations || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportOrganizations,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuwe organisatie",
                to: "/beheer/organisaties/aanmaken",
                entity: "organizations",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {isCreateContactOpen && activeRow && (
        <CreateOrganizationUserModal
          isOpen={isCreateContactOpen}
          setIsOpen={setIsCreateContactOpen}
          organizationId={String(activeRow.id)}
        />
      )}

      {editInfoOpen && activeRow && (
        <EditInfoModal
          editInfoOpen={editInfoOpen}
          setEditInfoOpen={setEditInfoOpen}
          activeRow={String(activeRow.id)}
          organizationData={activeRow}
          closeDialog={closeEditInfoDialog}
        />
      )}

      {editStyleOpen && activeRow && (
        <EditStyleModal
          editStyleOpen={editStyleOpen}
          setEditStyleOpen={setEditStyleOpen}
          activeRow={String(activeRow.id)}
          closeDialog={closeEditStyleDialog}
        />
      )}

      {isArchiveOpen && activeRow && (
        <ArchiveOrganizationModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          organization={activeRow}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveOrganizationModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          organization={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
